import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getListTwinPresenceQueryOptions,
  leaveTwinPresence,
  updateTwinPresence,
  type TwinPresence,
  type TwinPresenceInput,
} from '@cdf/api-client-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserLocationTrack, DEFAULT_LOCATION_UPDATE_INTERVAL_SECONDS, type UserLocation } from '@/hooks/use-user-location-track';
import { validateAvatarDisplayName } from '@/lib/profanity';

const DEFAULT_AVATAR_COLOR = '#2563eb';
const PRESENCE_TOKEN_KEY_PREFIX = 'cdf:twin-presence:';
const HEARTBEAT_INTERVAL_MS = 10_000;
const EMPTY_PARTICIPANTS: TwinPresence[] = [];

export type TwinPresenceCoordinates = Pick<TwinPresenceInput, 'latitude' | 'longitude' | 'floorX' | 'floorZ' | 'accuracyMeters'>;

export interface TwinPresenceControllerOptions {
  sceneId: string;
  /** Converts a device fix to the coordinate system used by this twin scene. */
  projectLocation: (location: UserLocation) => TwinPresenceCoordinates;
}

export interface TwinPresenceController {
  displayName: string;
  avatarColor: string;
  updateIntervalSeconds: number;
  isSharing: boolean;
  locationTrack: ReturnType<typeof useUserLocationTrack>;
  participants: TwinPresence[];
  error: string | null;
  setDisplayName: (value: string) => void;
  setAvatarColor: (value: string) => void;
  setUpdateIntervalSeconds: (value: number) => void;
  join: () => void;
  leave: () => Promise<void>;
}

function createParticipantId(sceneId: string): string {
  const key = `${PRESENCE_TOKEN_KEY_PREFIX}${sceneId}:participant`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;

  const participantId = crypto.randomUUID();
  sessionStorage.setItem(key, participantId);
  return participantId;
}

function getToken(sceneId: string): string | null {
  return sessionStorage.getItem(`${PRESENCE_TOKEN_KEY_PREFIX}${sceneId}:token`);
}

function setToken(sceneId: string, token: string | null): void {
  const key = `${PRESENCE_TOKEN_KEY_PREFIX}${sceneId}:token`;
  if (token) sessionStorage.setItem(key, token);
  else sessionStorage.removeItem(key);
}

/**
 * Coordinates private browser geolocation, presence heartbeats, and public
 * participant polling for one digital-twin scene.
 */
export function useTwinPresence({ sceneId, projectLocation }: TwinPresenceControllerOptions): TwinPresenceController {
  const queryClient = useQueryClient();
  const projectLocationRef = useRef(projectLocation);
  const tokenRef = useRef<string | null>(null);
  const publishQueueRef = useRef<Promise<void>>(Promise.resolve());
  const isSharingRef = useRef(false);
  const activeSceneRef = useRef(sceneId);
  const activeParticipantIdRef = useRef<string | null>(null);
  const publishedProfileRef = useRef<string | null>(null);
  const settingsRef = useRef({
    displayName: 'Visitor',
    avatarColor: DEFAULT_AVATAR_COLOR,
    updateIntervalSeconds: DEFAULT_LOCATION_UPDATE_INTERVAL_SECONDS,
  });
  const [displayName, setDisplayName] = useState('Visitor');
  const [avatarColor, setAvatarColor] = useState(DEFAULT_AVATAR_COLOR);
  const [updateIntervalSeconds, setUpdateIntervalSeconds] = useState(DEFAULT_LOCATION_UPDATE_INTERVAL_SECONDS);
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  projectLocationRef.current = projectLocation;
  settingsRef.current = { displayName, avatarColor, updateIntervalSeconds };

  const participantId = useMemo(() => createParticipantId(sceneId), [sceneId]);
  const queryParams = useMemo(() => ({ sceneId, excludeSelf: participantId }), [sceneId, participantId]);
  const presenceQuery = useQuery({
    ...getListTwinPresenceQueryOptions(queryParams),
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });

  const publishFix = useCallback((location: UserLocation) => {
    const publish = async (): Promise<void> => {
      if (!isSharingRef.current) return;

      const settings = settingsRef.current;
      const validName = validateAvatarDisplayName(settings.displayName);
      if (!validName.valid || !validName.value) {
        setError(validName.error ?? 'Choose a valid display name before sharing.');
        return;
      }

      try {
        const response = await updateTwinPresence({
          userId: participantId,
          sceneId,
          displayName: validName.value,
          avatarColor: settings.avatarColor,
          updateIntervalSec: settings.updateIntervalSeconds,
          ...projectLocationRef.current(location),
        }, {
          headers: tokenRef.current ? { 'x-presence-token': tokenRef.current } : undefined,
        });
        tokenRef.current = response.presenceToken;
        setToken(sceneId, response.presenceToken);
        setError(null);
        await queryClient.invalidateQueries({ queryKey: getListTwinPresenceQueryOptions(queryParams).queryKey });
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Unable to update your live location.');
      }
    };

    // The location watcher and the profile effect can both publish the first
    // fix. Keep writes ordered so later updates always receive the token
    // returned by the initial join request.
    const queuedPublish = publishQueueRef.current.then(publish, publish);
    publishQueueRef.current = queuedPublish;
    return queuedPublish;
  }, [participantId, queryClient, queryParams, sceneId]);

  const locationTrack = useUserLocationTrack({
    intervalSeconds: updateIntervalSeconds,
    onLocation: publishFix,
  });

  const leave = useCallback(async () => {
    const token = tokenRef.current;
    const leavingScene = activeSceneRef.current;
    const leavingParticipantId = activeParticipantIdRef.current ?? participantId;
    isSharingRef.current = false;
    publishedProfileRef.current = null;
    setIsSharing(false);
    locationTrack.stop();

    if (!token) return;

    try {
      await leaveTwinPresence(leavingParticipantId, { sceneId: leavingScene }, { headers: { 'x-presence-token': token } });
      if (tokenRef.current === token) {
        tokenRef.current = null;
        setToken(leavingScene, null);
      }
      await queryClient.invalidateQueries({ queryKey: getListTwinPresenceQueryOptions(queryParams).queryKey });
    } catch (cause) {
      // Keep the token for a later retry: the server requires it while this
      // short-lived participant record is still active.
      setError(cause instanceof Error ? cause.message : 'Unable to stop location sharing. Please try again.');
    }
  }, [locationTrack, participantId, queryClient, queryParams]);

  const join = useCallback(() => {
    const validName = validateAvatarDisplayName(displayName);
    if (!validName.valid || !validName.value) {
      setError(validName.error ?? 'Choose a valid display name before sharing.');
      return;
    }

    tokenRef.current = getToken(sceneId);
    activeSceneRef.current = sceneId;
    activeParticipantIdRef.current = participantId;
    isSharingRef.current = true;
    setDisplayName(validName.value);
    setError(null);
    setIsSharing(true);
    locationTrack.start();
  }, [displayName, locationTrack, participantId, sceneId]);

  // A location fix is emitted on the selected cadence, but a shorter heartbeat
  // retains the most recent position before the API's 30-second expiry window.
  useEffect(() => {
    if (!isSharing || !locationTrack.location) return;
    const heartbeat = window.setInterval(() => {
      void publishFix(locationTrack.location!);
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(heartbeat);
  }, [isSharing, locationTrack.location, publishFix]);

  // Apply profile changes promptly instead of waiting for the next GPS fix.
  useEffect(() => {
    if (!isSharing || !locationTrack.location) return;
    const signature = `${displayName}\u0000${avatarColor}\u0000${updateIntervalSeconds}`;
    if (publishedProfileRef.current === signature) return;
    publishedProfileRef.current = signature;
    void publishFix(locationTrack.location);
  }, [avatarColor, displayName, isSharing, locationTrack.location, publishFix, updateIntervalSeconds]);

  useEffect(() => {
    if (isSharing && (locationTrack.status === 'denied' || locationTrack.status === 'unsupported')) {
      void leave();
    }
  }, [isSharing, leave, locationTrack.status]);

  useEffect(() => {
    if (activeSceneRef.current === sceneId) return;

    const previousScene = activeSceneRef.current;
    const previousParticipantId = activeParticipantIdRef.current;
    const previousToken = tokenRef.current;
    if (previousToken && previousParticipantId) {
      void leaveTwinPresence(previousParticipantId, { sceneId: previousScene }, { headers: { 'x-presence-token': previousToken } });
    }

    tokenRef.current = getToken(sceneId);
    activeSceneRef.current = sceneId;
    activeParticipantIdRef.current = participantId;
    isSharingRef.current = false;
    publishedProfileRef.current = null;
    setIsSharing(false);
    locationTrack.stop();
  }, [locationTrack.stop, participantId, sceneId]);

  useEffect(() => () => {
    const token = tokenRef.current;
    const activeParticipantId = activeParticipantIdRef.current;
    if (token && activeParticipantId) {
      void leaveTwinPresence(activeParticipantId, { sceneId: activeSceneRef.current }, { headers: { 'x-presence-token': token } });
    }
  }, []);

  return {
    displayName,
    avatarColor,
    updateIntervalSeconds,
    isSharing,
    locationTrack,
    participants: presenceQuery.data?.participants ?? EMPTY_PARTICIPANTS,
    error,
    setDisplayName,
    setAvatarColor,
    setUpdateIntervalSeconds: (value) => setUpdateIntervalSeconds(Math.max(1, Math.min(30, value))),
    join,
    leave,
  };
}
